import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { CORE_SERVICES, HttpMethods } from '../../../services/api';
import { ThunkAPI } from '../../rootReducer';
import apiAsyncThunkCreator, { basicApiAsyncThunkCreator } from '../../../services/apiAsyncThunkCreator';
import { IDrawdownDealResponse, IDrawdownDealItemStore, IUpsertPendingDrawdownPayload, IUpsertFinalizedPendingDrawdownPayload, IDeleteDrawdownPayload, DealStateResponse, DrawdownStatus } from './interfaces';
import { showSuccessMessage } from '../../notifications';
import { fetchCreditPoolById } from '../creditPool/creditPool.thunks';

type DrawdownId = string;

export const fetchDrawdownById:AsyncThunk<IDrawdownDealItemStore, DrawdownId, ThunkAPI> = createAsyncThunk(
  'drawdown/getById',
  async (drawdownId, thunk) => {
    const response = await apiAsyncThunkCreator<{ drawdown: IDrawdownDealResponse }, void>({
      endpoint: `credit-pool/draw-down/${drawdownId}`,
      microservice: CORE_SERVICES.CREDITS_SERVICE,
      authenticated: true,
      method: HttpMethods.Get
    })(undefined, thunk);

    if (!('drawdown' in response)) {
      throw new Error('Response is not correct');
    }

    const { drawdown: { _id: id, projectId, drawdownAmount, status: statusFromResponse, deal: { id: dealId, name, type, closeDate: closeDateStr, contacts, contactSxIds, amount, lineItems } } } = response;

    const closeDate = new Date(closeDateStr);

    const closeDateFormatted = closeDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    } as any);

    const status: DrawdownStatus = statusFromResponse === DealStateResponse.Final.toString() ? DrawdownStatus.Final : DrawdownStatus.Pending;

    const result:IDrawdownDealItemStore = {
      id,
      projectId,
      amountInCents: drawdownAmount * 100,
      deal: {
        id: dealId,
        name,
        type,
        closeDate: closeDateFormatted,
        contacts,
        contactSxIds,
        totalDealAmountInCents: amount * 100,
        lineItems: lineItems.map(({ description, quantity, price, amount }) => ({
          description: description,
          quantity,
          priceInCents: price * 100,
          lineTotalInCents: amount * 100
        })),
        status
      }
    };

    return result;
  }
);

export const fetchDrawdownDealByAccounIdAndDealId:AsyncThunk<IDrawdownDealItemStore, { dealId: string; accountId: string }, ThunkAPI> = createAsyncThunk(
  'drawdown/deal/getByIdAndAccountId',
  async ({ dealId: dealIdParam, accountId }, thunk) => {
    const response = await apiAsyncThunkCreator<IDrawdownDealResponse, void>({
      endpoint: `credit-pool/draw-down/deal/${dealIdParam}/company/${accountId}`,
      microservice: CORE_SERVICES.CREDITS_SERVICE,
      authenticated: true,
      method: HttpMethods.Get
    })(undefined, thunk);

    if (!('deal' in response)) {
      throw new Error('Response is not correct');
    }

    const {
      _id: id,
      drawdownAmount,
      projectId,
      status: statusFromResponse,
      deal: {
        id: dealId,
        name,
        type,
        closeDate: closeDateStr,
        contacts,
        contactSxIds,
        amount,
        lineItems
      }
    } = response;

    const closeDate = new Date(closeDateStr);

    const closeDateFormatted = closeDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    } as any);

    const status: DrawdownStatus = statusFromResponse === DealStateResponse.Final.toString() ? DrawdownStatus.Final : DrawdownStatus.Pending;

    const result: IDrawdownDealItemStore = {
      id,
      projectId,
      amountInCents: drawdownAmount * 100,
      deal: {
        id: dealId,
        name,
        type,
        closeDate: closeDateFormatted,
        contacts,
        contactSxIds,
        totalDealAmountInCents: amount * 100,
        lineItems: lineItems.map(({ description, quantity, price, amount }) => ({
          description: description,
          quantity,
          priceInCents: price * 100,
          lineTotalInCents: amount * 100
        })),
        status
      }
    };

    return result;
  }
);

export const upsertPendingDrawdown:AsyncThunk<void, IUpsertPendingDrawdownPayload, ThunkAPI> = createAsyncThunk(
  'upsert/pending-drawdown',
  async (payload, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, IUpsertPendingDrawdownPayload>({
        endpoint: 'credit-pool/draw-down/pending',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })(payload, thunk);

      thunk.dispatch(showSuccessMessage('Draw down has been successfully updated'));
      thunk.dispatch(fetchCreditPoolById(payload.creditPoolId));

      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);

export const upsertFinalizedDrawdown:AsyncThunk<void, IUpsertFinalizedPendingDrawdownPayload, ThunkAPI> = createAsyncThunk(
  'upsert/finalized-drawdown',
  async (payload, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, IUpsertFinalizedPendingDrawdownPayload>({
        endpoint: 'credit-pool/draw-down/finalize',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })(payload, thunk);

      thunk.dispatch(showSuccessMessage('Draw down has been successfully updated'));
      thunk.dispatch(fetchCreditPoolById(payload.creditPoolId));

      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);

export const deleteDrawdown:AsyncThunk<void, IDeleteDrawdownPayload, ThunkAPI> = createAsyncThunk(
  'delete/draw-down',
  async (payload, thunk) => {
    const data = {
      dealId: payload.dealId
    };

    try {
      const response = await basicApiAsyncThunkCreator<void, Omit<IDeleteDrawdownPayload, 'creditPoolId'>>({
        endpoint: 'credit-pool/draw-down/remove',
        microservice: CORE_SERVICES.CREDITS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post
      })(data, thunk);

      thunk.dispatch(showSuccessMessage('Draw down has been successfully removed'));
      thunk.dispatch(fetchCreditPoolById(payload.creditPoolId));

      return response;
    } catch (err) {
      return thunk.rejectWithValue(err);
    }
  }
);
